<script setup>
import { onBeforeUnmount, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { useUiStore } from "@/app/stores/uiStore";
import { debounce } from "lodash";

// My Components
import HeaderTitle from "@/components/layout/HeaderTitle";
import SharedElementPagination from "@/components/shared/SharedElementPagination.vue";

// Services
import VehicleService from '@/api/services/vehicle.service';

// Icons Components
import IconVehicle from '@/components/icons/IconVehicle.vue';

const vehicles = ref([])

const vehicleLicensePlate = ref();
const vehicleStatus = ref();
const statusOptions = ref([
    { label: 'Activo', value: true },
    { label: 'Inactivo', value: false },
]);

const selectedFromDate = ref(null);
const selectedToDate = ref(null);

const params = ref({})
const page = ref(1)
const itemsPerPage = ref(10);
const totalResults = ref(0);

const uiStore = useUiStore();
// eslint-disable-next-line no-unused-vars
const router = useRouter();

onMounted(() => {
    params.value = {};
    getVehicles();
})

onBeforeUnmount(() => {
    debouncedSearch.cancel();
});

// Search bar
const search = () => {
    params.value = {};
    params.value.licensePlate = vehicleLicensePlate;
    params.value.active = vehicleStatus;
    params.value.dateFrom = selectedFromDate;
    params.value.dateTo = selectedToDate;
    getVehicles();
}

const debouncedSearch = debounce(() => {
    search();
}, 300);

// Getter
const getVehicles = async () => {
    params.value.page = page.value;
    params.value.itemsPerPage = itemsPerPage.value;
    uiStore.startLoading();
    VehicleService.getVehicles(params.value)
        .then((res) => {
            vehicles.value = res.data['hydra:member']
            totalResults.value = res.data['hydra:totalItems'];
            // uiStore.setLoading(false);
            uiStore.stopLoading();
        })
        .catch(() => {
            uiStore.stopLoading();
        })
}

// Pagination
const onPageChange = (newPage) => {
    page.value = newPage;
    getVehicles();
};

const onSizeChange = (newSize) => {
    itemsPerPage.value = newSize;
    if (page.value > Math.ceil(totalResults.value / newSize)) {
        page.value = 1;
    }
    getVehicles();
};

const openVehicleItem = (selected = null) => {
    if (selected) {
        router.push({ path: '/vehicle/' + selected.id })
    } else {
        router.push({ path: '/vehicle/new' })
    }
}


// Button Adblue
const getStyleAdblue = (v) => {
    const style = {
        color: '#fff'
    }
    if (v.requiresAdBlue && v.needsAdBlue) {
        style.background = '#ff3030'
    }
    if (v.requiresAdBlue && v.refuelCountSinceAdBlue === 3) {
        style.background = '#e6a23c'
    }
    if (v.requiresAdBlue && v.refuelCountSinceAdBlue < 3) {
        style.background = '#67c23a'
    }
    return style;
}
</script>

<template>
    <div>
        <HeaderTitle title="Vehículos" />
        <div class="d-flex flex-row justify-content-start">
            <div class="col-md-4 d-flex">
                <IconVehicle class="iconVehicle" />
                <div class="title-text ml-4">
                    Vehículos
                </div>
            </div>
        </div>
        <el-row class="mt-2">
            <el-card class="card-search card-abs w-100 wback" body-style="padding:0">
                <div class="search-form">
                    <div class="row vehicle-search-row">

                        <div class="col-md-3 el-col-xs-24">
                            <div class="form-item">
                                <label>Matrícula</label>
                                <el-input v-model="vehicleLicensePlate" placeholder="Buscar por matrícula" clearable
                                    @input="debouncedSearch()">
                                </el-input>
                            </div>
                        </div>
                        <div class="col-md-3 el-col-xs-24">
                            <div class="form-item">
                                <label class="w-100">Estado</label>
                                <el-select v-model="vehicleStatus" placeholder="Todos" filterable clearable
                                    @change="search()">
                                    <el-option v-for="u in statusOptions" :key="u.value" :label="u.label"
                                        :value="u.value">
                                    </el-option>
                                </el-select>
                            </div>
                        </div>
                        <div class="col-md-6 el-col-xs-24 row d-flex vehicle-search-actions">
                            <div
                                class="d-flex justify-content-end align-items-center col-md-12 vehicle-search-actions__inner">
                                <div class="align-self-center justify-content-center">
                                    <button type="submit" @click="openVehicleItem()" class="btn btn_red btn__new"
                                        style="width: 200px;">
                                        añadir
                                    </button>
                                </div>
                                <div class="align-self-center justify-content-center">
                                    <button type="submit" @click="search()" class="btn btn_yellow btn__search"
                                        style="width: 200px;">
                                        BUSCAR
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>

            </el-card>
        </el-row>
        <div class="table table-users table-not-mobile">
            <div v-if="vehicles && vehicles.length > 0">
                <table>
                    <thead class="table-header">
                        <tr class="justify-content-start gap-2">
                            <th>
                                MATRÍCULA
                            </th>
                            <th>
                                ALIAS
                            </th>
                            <th>
                                NOTAS
                            </th>
                            <th>
                                ASIENTOS
                            </th>
                            <th>
                                ESTADO
                            </th>
                            <th>
                                ADBLUE
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="v in vehicles" :key="v.id">
                            <td class="pointer" @click="openVehicleItem(v)">
                                {{ v.licensePlate }}
                            </td>
                            <td class="pointer" @click="openVehicleItem(v)">
                                {{ v.displayName }}
                            </td>
                            <td class="pointer" @click="openVehicleItem(v)">
                                {{ v.notes }}
                            </td>
                            <td class="pointer" @click="openVehicleItem(v)">
                                <!-- No estan los kms -->
                                {{ v.totalSeats }}
                            </td>
                            <td class="pointer" @click="openVehicleItem(v)">
                                <!-- No estan el status -->
                                {{ v.active ? 'Activo' : 'Inactivo' }}
                            </td>
                            <td @click="openVehicleItem(v)" class="pointer text-center">
                                <div v-if="v.fuelType === 'diesel' && v.requiresAdBlue">
                                    <el-button :style="getStyleAdblue(v)">
                                        <el-icon style="margin-right: 5px;">
                                            <WarningFilled v-if="v.refuelCountSinceAdBlue >= 4" />
                                            <WarnTriangleFilled v-if="v.refuelCountSinceAdBlue === 3" />
                                            <Select v-if="v.refuelCountSinceAdBlue < 3" />
                                        </el-icon> {{ v.refuelCountSinceAdBlue }}/4
                                    </el-button>
                                </div>
                            </td>
                            <td>
                                <!-- <div class="buttons d-inline-flex" v-if="user && ifIsInArray('ROLE_SUPER_ADMIN', user.rol)">-->
                                <div class="buttons d-inline-flex">
                                    <div class="pointer">
                                        <font-awesome-layers class="fa-2x" style="margin-left: 5px" title="Editar"
                                            @click="openVehicleItem(v)">
                                            <font-awesome-icon icon="circle" style="color: #f7d20e" />
                                            <font-awesome-icon icon="plus" class="fa-inverse" transform="shrink-6" />
                                        </font-awesome-layers>
                                    </div>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div v-else>
                <h5>No se han encontrado Vehículos según los filtros especificados...</h5>
            </div>
        </div>
        <div class="table-mobile mb-4 mt-3">
            <div v-if="vehicles && vehicles.length > 0">
                <div v-for="v in vehicles" :key="v.id">
                    <el-row class="mt-3">
                        <el-card class="card-vehicle-item">
                            <div class="vehicle-card-content pointer" @click="openVehicleItem(v)">
                                <div class="buttons d-inline-flex justify-content-end vehicle-card-actions">
                                    <div class="pointer">
                                        <font-awesome-layers class="fa-2x" style="margin-left: 5px; margin-top: 7px;"
                                            title="Editar" @click.stop="openVehicleItem(v)">
                                            <font-awesome-icon icon="circle" style="color: #f7d20e" />
                                            <font-awesome-icon icon="plus" class="fa-inverse" transform="shrink-6" />
                                        </font-awesome-layers>
                                    </div>
                                </div>
                                <div class="el-col-xs-24">
                                    <span class="vehicle-item-label">Matrícula: </span>{{ v.licensePlate }}
                                </div>
                                <div class="el-col-xs-24">
                                    <span class="vehicle-item-label">Alias: </span>{{ v.displayName }}
                                </div>
                                <div class="el-col-xs-24 vehicle-inline-row">
                                    <span><span class="vehicle-item-label">Asientos: </span>{{ v.totalSeats }}</span>
                                    <span><span class="vehicle-item-label">Estado: </span>{{ v.active ? 'Activo' :
                                        'Inactivo'
                                        }}</span>
                                </div>
                                <div class="el-col-xs-24" v-if="v.fuelType === 'diesel' && v.requiresAdBlue">
                                    <span class="vehicle-item-label">AdBlue: </span>
                                    <el-button :style="getStyleAdblue(v)" class="vehicle-adblue-button">
                                        <el-icon style="margin-right: 5px;">
                                            <WarningFilled v-if="v.refuelCountSinceAdBlue >= 4" />
                                            <WarnTriangleFilled v-if="v.refuelCountSinceAdBlue === 3" />
                                            <Select v-if="v.refuelCountSinceAdBlue < 3" />
                                        </el-icon>
                                        {{ v.refuelCountSinceAdBlue }}/4
                                    </el-button>
                                </div>
                                <div class="el-col-xs-24" v-if="v.notes">
                                    <span class="vehicle-item-label">Notas: </span>{{ v.notes }}
                                </div>
                            </div>
                        </el-card>
                    </el-row>
                </div>
            </div>
            <div v-else>
                <h5>No se han encontrado Vehículos según los filtros especificados...</h5>
            </div>
        </div>
        <SharedElementPagination v-model:current-page="page" v-model:page-size="itemsPerPage" size="large"
            :page-size-options="[10, 50, 100, 200]" layout="prev, pager, next, sizes" :total="totalResults"
            class="tracker-pagination" show-always @current-change="onPageChange" @size-change="onSizeChange" />
    </div>
</template>

<style lang="scss" scoped>
.btn__search {
    color: $bg-blue;
    text-transform: uppercase;
    font-weight: bolder;
}

.card-search {
    border-radius: 15px;
    height: 150px;
    background: url("../../assets/icons/car.jpg"), white;
    background-repeat: no-repeat;
    background-position-x: 100%;
    background-position-y: 69%;
    overflow: visible !important;
    background-size: 26%;
}

.btn__new {
    text-transform: uppercase;
    font-weight: bolder;
}

.iconVehicle {
    width: 30px;
    fill: $bg-blue;
}

.table-header {

    th,
    tr {
        vertical-align: middle;
    }
}

.table-mobile {
    display: none;
}

.vehicle-item-label {
    color: $bg-blue;
    font-weight: 700;
}

.card-vehicle-item {
    border-radius: 18px;
}

.vehicle-card-content {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding-top: 8px;
    padding-bottom: 10px;
}

.vehicle-card-actions {
    position: absolute;
    top: 0;
    right: 0;
    flex-shrink: 0;
}

.vehicle-adblue-button {
    margin-top: 6px;
}

.vehicle-inline-row {
    display: flex;
    gap: 18px;
    flex-wrap: wrap;
}

@media (max-width: 767px) {
    .card-search {
        height: auto;
        background-size: 34%;
        background-position-y: bottom;
    }

    .vehicle-search-row {
        row-gap: 6px;
    }

    .vehicle-search-actions {
        margin-top: 8px;
    }

    .vehicle-search-actions__inner {
        flex-direction: column;
        gap: 12px;
        align-items: stretch !important;
    }

    .btn__new,
    .btn__search {
        width: 70% !important;
        min-width: 210px;
        margin-top: 0;
        align-self: center;
    }

    .table-not-mobile {
        display: none;
    }

    .table-mobile {
        display: block;
    }
}

@media (min-width: 768px) {
    .table-mobile {
        display: none;
    }
}
</style>
